import React, { Component } from "react";
import { Tooltip, message } from "antd";
import { PushpinOutlined, PushpinFilled } from "@ant-design/icons";
import { ButtonItem, SelectEmpleadosItem } from "../../../../Components/Items";
import { cmmsPost } from "../cmmsComun";

// Filtro de ingeniero con "fijar": cada usuario puede fijar un ingeniero (normalmente él mismo)
// y en todo el CMMS (Servicios, Agenda, Tickets, reportes; no en la calendarizacion del contrato) arranca
// viendo solo lo de ese ingeniero. Es una preferencia, no un candado: en cada pantalla se
// puede quitar el filtro. Se guarda en el servidor (sigue al usuario en cualquier equipo) y
// aqui se recuerda durante la sesion.

const VACIO = { empleadoId: null, empleadoNombre: "", ingenieroId: null, ingenieroNombre: "" };
let cache = { clave: null, promesa: null };
const suscriptores = new Set();

const claveDe = (props) => `${props.empresaId}|${JSON.stringify(props.headersToken || {})}`;

// { empleadoId, empleadoNombre } = filtro fijado; { ingenieroId, ingenieroNombre } = el ingeniero
// ligado al usuario (Configuración CMMS › Ingenieros).
export const miFiltroCMMS = (props) => {
    const clave = claveDe(props);
    if (cache.clave !== clave || !cache.promesa) {
        const promesa = cmmsPost(props, "miIngenieroCMMS")
            .then((res) =>
                res.data.success === 2
                    ? {
                          empleadoId: res.data.filtroEmpleadoId || null,
                          empleadoNombre: res.data.filtroEmpleadoNombre || "",
                          ingenieroId: res.data.empleadoId || null,
                          ingenieroNombre: res.data.empleadoNombre || "",
                      }
                    : VACIO
            )
            .catch((error) => {
                console.log(error);
                // Sin respuesta no se recuerda: se reintenta en la siguiente pantalla.
                if (cache.promesa === promesa) cache = { clave: null, promesa: null };
                return VACIO;
            });
        cache = { clave, promesa };
    }
    return cache.promesa;
};

const guardarMiFiltro = async (props, empleadoId) => {
    const res = await cmmsPost(props, "guardarMiFiltroCMMS", { empleadoId: empleadoId || null });
    if (res.data.success !== 2) throw new Error(res.data.message || "No se pudo guardar");
    // Se vuelve a leer del servidor (trae el nombre) y se avisa a los filtros montados.
    cache = { clave: null, promesa: null };
    const nuevo = await miFiltroCMMS(props);
    suscriptores.forEach((fn) => fn(nuevo));
};

// Pone el filtro fijado en el estado de la pantalla (si no trae ya uno) antes de su primera
// consulta. Devuelve siempre la misma promesa para esa pantalla.
export const esperarFiltroDefault = (componente, campo = "empleadoId", campoNombre = null) => {
    if (!componente.__filtroDefaultCMMS)
        componente.__filtroDefaultCMMS = miFiltroCMMS(componente.props).then(
            (f) =>
                new Promise((resolve) => {
                    if (!f.empleadoId || componente.state[campo]) return resolve(f);
                    componente.setState({ [campo]: f.empleadoId, ...(campoNombre ? { [campoNombre]: f.empleadoNombre } : {}) }, () => resolve(f));
                })
        );
    return componente.__filtroDefaultCMMS;
};

// Selector de ingeniero + chincheta para fijarlo. Props: value, onChange(id, empleado),
// placeholder, width, más empresaId/headersToken.
export class FiltroIngenieroCMMS extends Component {
    state = { fijo: null, fijoNombre: "", guardando: false };

    componentDidMount() {
        this.vivo = true;
        suscriptores.add(this.alCambiar);
        miFiltroCMMS(this.props).then(this.alCambiar);
    }

    componentWillUnmount() {
        this.vivo = false;
        suscriptores.delete(this.alCambiar);
    }

    alCambiar = (f) => this.vivo && this.setState({ fijo: f.empleadoId || null, fijoNombre: f.empleadoNombre || "" });

    fijar = async (empleadoId) => {
        this.setState({ guardando: true });
        try {
            await guardarMiFiltro(this.props, empleadoId);
            message.success(empleadoId ? "Listo: en todo el CMMS verá primero sólo lo de este ingeniero" : "Se quitó su filtro por default");
        } catch (error) {
            console.log(error);
            message.error(error.message || "No se pudo guardar");
        } finally {
            if (this.vivo) this.setState({ guardando: false });
        }
    };

    render() {
        const { value, onChange, placeholder = "Todos los ingenieros", width = 260, empresaId, headersToken } = this.props;
        const { fijo, fijoNombre, guardando } = this.state;
        const esFijo = !!fijo && !!value && String(value) === String(fijo);
        let titulo;
        let accion;
        if (esFijo) {
            titulo = "Es su filtro por default: en todo el CMMS arranca viendo sólo lo de este ingeniero. Clic para quitarlo.";
            accion = () => this.fijar(null);
        } else if (value) {
            titulo = `Fijar: ver siempre sólo lo de este ingeniero en todo el CMMS (Servicios, Agenda, Tickets...)${fijo ? `. Hoy está fijado ${fijoNombre || "otro"}.` : ""}`;
            accion = () => this.fijar(value);
        } else if (fijo) {
            titulo = `Quitar su filtro por default (${fijoNombre || "ingeniero fijado"}) para ver todo siempre`;
            accion = () => this.fijar(null);
        } else {
            titulo = "Elija un ingeniero (por ejemplo, usted) y fíjelo para ver siempre sólo lo suyo";
        }
        return (
            <div style={{ display: "flex", gap: 4, alignItems: "center", width }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                    <SelectEmpleadosItem
                        empresaId={empresaId}
                        headersToken={headersToken}
                        width="100%"
                        allowClear
                        placeholder={placeholder}
                        value={value || undefined}
                        onChange={(id, empleado) => onChange(id || null, empleado)}
                    />
                </div>
                <Tooltip title={titulo}>
                    <ButtonItem
                        type="default"
                        blanco={!esFijo}
                        icon={esFijo ? <PushpinFilled /> : <PushpinOutlined />}
                        loading={guardando}
                        disabled={!accion}
                        onClick={accion}
                    />
                </Tooltip>
            </div>
        );
    }
}

export default FiltroIngenieroCMMS;
